import {
    BarChart3,
    CheckCircle,
    Filter, Home, MapPin,
    Plus, Search,
    Settings,
    Wrench
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import UnitManagementModal from './UnitManagementModal';

const getUnitTypeText = (unitType) => {
  const normalized = String(unitType || '').trim().toLowerCase();
  const typeMap = {
    studio: '원룸',
    one_room: '1.5룸',
    '1room': '원룸',
    '1룸': '원룸',
    onehalf_room: '1.5룸',
    '1.5room': '1.5룸',
    '1.5룸': '1.5룸',
    two_room: '투룸',
    '2room': '투룸',
    '2룸': '투룸',
    투룸: '투룸',
    three_room: '쓰리룸 이상',
    four_room: '쓰리룸 이상',
    '3room': '쓰리룸 이상',
    '3룸': '쓰리룸 이상',
    '3룸이상': '쓰리룸 이상',
    '쓰리룸 이상': '쓰리룸 이상',
  };

  return typeMap[normalized] || unitType || '-';
};

const AllUnits = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [selectedUnitForManagement, setSelectedUnitForManagement] = useState(null);
  const [showManagementModal, setShowManagementModal] = useState(false);
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');
  const [selectedBuildingFilter, setSelectedBuildingFilter] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // 사용자 정보 가져오기 (RMC 사용자 확인용)
  const { data: userInfo } = useQuery(['user-info'], () =>
    api.get('/auth/me'),
    {
      onSuccess: (data) => {
        setCurrentUser(data);
      }
    }
  );

  // RMC 사용자 여부 확인
  const isRMCUser = userInfo?.role === 'rental_management_company' || 
                    userInfo?.role === 'rmc_admin' || 
                    userInfo?.role === 'rmc_staff';
  const isUserInfoReady = !!userInfo?.role;

  useEffect(() => {
    setSelectedBuildingFilter('');
  }, [selectedPropertyOwner]);

  const { data: buildingsData } = useQuery(
    ['unit-filter-buildings', selectedPropertyOwner],
    async () => {
      if (isRMCUser && !selectedPropertyOwner) {
        return [];
      }
      const url = selectedPropertyOwner
        ? `/buildings?property_owner_id=${selectedPropertyOwner}`
        : '/buildings';
      const response = await api.get(url);
      return Array.isArray(response) ? response : response?.buildings || response?.data || [];
    },
    {
      enabled: !isRMCUser || !!selectedPropertyOwner
    }
  );

  const buildingOptions = useMemo(() => buildingsData || [], [buildingsData]);

  // 임대사업자 목록 가져오기 (RMC 사용자용)
  const { data: propertyOwners } = useQuery(
    ['property-owners', userInfo?.role],
    () => {
      // RMC 사용자는 RMC 전용 API 사용
      if (isRMCUser) {
        return api.get('/rmc/property-owners');
      }
      return api.get('/property-owners');
    },
    {
      enabled: isRMCUser,
      staleTime: 5 * 60 * 1000, // 5분
      cacheTime: 10 * 60 * 1000, // 10분
    }
  );

  const propertyOwnerList = useMemo(
    () => propertyOwners?.property_owners || propertyOwners || [],
    [propertyOwners]
  );

  useEffect(() => {
    if (!isRMCUser || selectedPropertyOwner || propertyOwnerList.length === 0) {
      return;
    }
    setSelectedPropertyOwner(String(propertyOwnerList[0].id));
  }, [isRMCUser, propertyOwnerList, selectedPropertyOwner]);

  useEffect(() => {
    if (!isRMCUser || !selectedPropertyOwner || selectedBuildingFilter || buildingOptions.length === 0) {
      return;
    }
    setSelectedBuildingFilter(String(buildingOptions[0].id));
  }, [isRMCUser, selectedPropertyOwner, selectedBuildingFilter, buildingOptions]);

  // 모든 세대 목록 가져오기 (모든 사용자가 동일한 API 사용)
  const { data: unitsData, isLoading } = useQuery(
    ['all-units', selectedPropertyOwner, selectedBuildingFilter],
    () => {
      const params = new URLSearchParams();
      if (selectedPropertyOwner) {
        params.append('property_owner_id', selectedPropertyOwner);
      }
      if (selectedBuildingFilter) {
        params.append('building_id', selectedBuildingFilter);
      }
      const queryString = params.toString();
      const url = queryString ? `/buildings/units/all?${queryString}` : '/buildings/units/all';
      return api.get(url);
    },
    {
      enabled: isUserInfoReady && (!isRMCUser || (!!selectedPropertyOwner && !!selectedBuildingFilter))
    }
  );

  const units = unitsData?.units || [];

  // 선택된 세대의 통계 정보 가져오기
  const { data: unitStats } = useQuery(
    ['unit-stats', selectedUnit?.id],
    () => api.get(`/units/${selectedUnit.id}/statistics`),
    { enabled: !!selectedUnit && showStatistics }
  );

  // 검색 및 필터링
  const filteredUnits = units.filter(unit => {
    const unitTypeText = getUnitTypeText(unit.unit_type).toLowerCase();
    const matchesSearch = unit.unit_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.unit_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unitTypeText.includes(searchTerm.toLowerCase()) ||
      unit.building_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || unit.status === filterStatus;
    const matchesBuilding = !selectedBuildingFilter || String(unit.building_id) === String(selectedBuildingFilter);
    return matchesSearch && matchesFilter && matchesBuilding;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'occupied':
        return 'bg-blue-100 text-blue-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'reserved':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'available':
        return '임대 가능';
      case 'occupied':
        return '임대중';
      case 'maintenance':
        return '정비중';
      case 'reserved':
        return '예약중';
      default:
        return '알 수 없음';
    }
  };

  const getConditionColor = (grade) => {
    switch (grade) {
      case 'A':
        return 'text-green-600';
      case 'B':
        return 'text-blue-600';
      case 'C':
        return 'text-yellow-600';
      case 'D':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // 임대사업자 사용자 보호: RMC 전용 기능 숨김 (위에서 이미 정의됨)

  return (
    <div className="space-y-6">
      {/* 페이지 제목 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">세대관리</h1>
          <p className="mt-1 text-sm text-gray-500">
            소속 건물의 모든 세대 현황 및 관리
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowStatistics(!showStatistics)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            통계 보기
          </button>
          <button
            onClick={() => navigate(isRMCUser ? '/rmc/units/add' : '/units/add')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="mr-2 h-4 w-4" />
            세대 추가
          </button>
        </div>
      </div>

      {/* 전체 통계 카드 */}
      {showStatistics && (
        <div className="grid grid-cols-2 gap-3 md:gap-5">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Home className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">총 세대 수</dt>
                    <dd className="text-lg font-medium text-gray-900">{units.length || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">임대 가능</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {units.filter(unit => unit.status === 'available').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Wrench className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">정비중</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {units.filter(unit => unit.status === 'maintenance').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Settings className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">임대중</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {units.filter(unit => unit.status === 'occupied').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 검색 및 필터 */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          {isRMCUser && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">임대사업자</label>
              <select
                value={selectedPropertyOwner}
                onChange={(e) => setSelectedPropertyOwner(e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">전체 임대사업자</option>
                {propertyOwnerList.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.company_name || owner.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">건물</label>
            <select
              value={selectedBuildingFilter}
              onChange={(e) => setSelectedBuildingFilter(e.target.value)}
              disabled={isRMCUser && !selectedPropertyOwner}
              className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100"
            >
              <option value="">{isRMCUser && !selectedPropertyOwner ? '임대사업자 먼저 선택' : '전체 건물'}</option>
              {buildingOptions.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1">검색</label>
            <div className="absolute left-0 bottom-0 pl-3 h-10 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="건물명, 호수, 세대유형"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1">상태</label>
            <div className="absolute left-0 bottom-0 pl-3 h-10 flex items-center pointer-events-none">
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">전체 상태</option>
              <option value="available">임대 가능</option>
              <option value="occupied">임대중</option>
              <option value="maintenance">정비중</option>
              <option value="reserved">예약중</option>
            </select>
          </div>

          <div className="flex items-end justify-end">
            <p className="text-sm text-gray-500">총 {filteredUnits.length}개 세대</p>
          </div>
        </div>
      </div>

      {/* 세대 목록 */}
      <div className="grid grid-cols-2 gap-3 md:gap-6 lg:grid-cols-3">
        {filteredUnits.map((unit) => (
          <div key={unit.id} className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
            {/* 세대 헤더 */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-3 py-3 md:px-6 md:py-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-white md:text-lg">{unit.building_name}</h3>
                  <p className="text-xs text-primary-100 md:text-sm">{unit.unit_number}호</p>
                </div>
                <span className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium md:px-2.5 md:text-xs ${getStatusColor(unit.status)}`}>
                  {getStatusText(unit.status)}
                </span>
              </div>
              <div className="mt-2 flex items-center truncate text-xs text-primary-100 md:text-sm">
                <MapPin className="mr-1 h-3 w-3 shrink-0 md:h-4 md:w-4" />
                {unit.floor}층
                {unit.area && (
                  <>
                    <span className="mx-1 md:mx-2">•</span>
                    {unit.area}㎡
                  </>
                )}
              </div>
            </div>

            {/* 세대 정보 */}
            <div className="p-3 md:p-6">
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <div className="rounded-md bg-gray-50 p-2">
                  <span className="block text-[11px] text-gray-500 md:text-sm">타입</span>
                  <span className="block truncate text-xs font-semibold text-gray-900 md:text-sm">{getUnitTypeText(unit.unit_type)}</span>
                </div>

                {unit.condition_grade && (
                  <div className="rounded-md bg-gray-50 p-2">
                    <span className="block text-[11px] text-gray-500 md:text-sm">상태</span>
                    <span className={`block text-xs font-semibold md:text-sm ${getConditionColor(unit.condition_grade)}`}>
                      {unit.condition_grade}등급
                    </span>
                  </div>
                )}

                {unit.monthly_rent && (
                  <div className="rounded-md bg-gray-50 p-2">
                    <span className="block text-[11px] text-gray-500 md:text-sm">월세</span>
                    <span className="block truncate text-xs font-semibold text-gray-900 md:text-sm">
                      {unit.monthly_rent.toLocaleString()}만원
                    </span>
                  </div>
                )}

                {unit.deposit && (
                  <div className="rounded-md bg-gray-50 p-2">
                    <span className="block text-[11px] text-gray-500 md:text-sm">보증금</span>
                    <span className="block truncate text-xs font-semibold text-gray-900 md:text-sm">
                      {unit.deposit.toLocaleString()}만원
                    </span>
                  </div>
                )}

                {unit.maintenance_fee && (
                  <div className="rounded-md bg-gray-50 p-2">
                    <span className="block text-[11px] text-gray-500 md:text-sm">관리비</span>
                    <span className="block truncate text-xs font-semibold text-gray-900 md:text-sm">
                      {unit.maintenance_fee.toLocaleString()}원
                    </span>
                  </div>
                )}
              </div>

              {/* 액션 버튼 */}
              <div className="mt-3 md:mt-6">
                <button
                  onClick={() => {
                    setSelectedUnitForManagement(unit);
                    setShowManagementModal(true);
                  }}
                  className="inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 md:px-3 md:py-2 md:text-sm"
                >
                  <Settings className="mr-1 h-3 w-3 md:h-4 md:w-4" />
                  상세정보
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 빈 상태 */}
      {filteredUnits.length === 0 && (
        <div className="text-center py-12">
          <Home className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">세대가 없습니다</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterStatus !== 'all'
              ? '검색 조건에 맞는 세대가 없습니다.'
              : '소속 건물에 세대를 추가해보세요.'}
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <div className="mt-6">
              <button
                onClick={() => navigate(isRMCUser ? '/rmc/buildings' : '/buildings')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="mr-2 h-4 w-4" />
                건물 관리
              </button>
            </div>
          )}
        </div>
      )}

      {/* 세대 관리 모달 */}
      <UnitManagementModal
        unit={selectedUnitForManagement}
        buildingId={selectedUnitForManagement?.building_id}
        isOpen={showManagementModal}
        onClose={() => {
          setShowManagementModal(false);
          setSelectedUnitForManagement(null);
        }}
        onUnitUpdated={() => {
          // 세대 정보가 업데이트되면 목록을 새로고침
        }}
      />
    </div>
  );
};

export default AllUnits;
